import { useCallback, useRef, useState } from "react";
import axios from "axios";
import {
  getOldPartIds,
  getOldCbdFile,
  LEGACY_TOKEN_EXPIRED,
  type MigrationVersion,
} from "@/api/migrationService";

import { parsePartFromExcel, submitImportPart } from "@/utils/excel/importPart";
import {
  parsePartV1FromExcel,
  submitImportPartV1,
} from "@/utils/excel/importPartV1";
import {
  toFileName,
  fileExists,
  writeXlsx,
  listXlsx,
  readAsFile,
  deleteXlsx,
  parseFileName,
  base64ToUint8Array,
} from "@/utils/excel/migrationFileStore";

export type { MigrationVersion };

/**
 * Batch runner สำหรับ migrate part+childpart ระบบเก่า (admin tool, one-shot)
 *
 * 2 โหมดการทำงาน:
 *
 * Phase A — Pull (startPull):
 *   ดึง Excel จากระบบเก่าผ่าน BE local → เขียนลงโฟลเดอร์ในเครื่อง
 *   โฟลเดอร์ = checkpoint (fileExists check) → ปิด browser เปิดใหม่ทำต่อได้
 *   ต้องใช้ Legacy Token · BE local ต้องเปิดอยู่
 *
 * Phase B — Import (startImport):
 *   อ่าน Excel จากโฟลเดอร์ → parse → import เข้าระบบใหม่ผ่าน BE Azure
 *   ไม่ต้องใช้ token · checkpoint ผ่าน localStorage (เฉพาะ commit mode)
 *
 * ทั้งสอง phase ใช้ infrastructure เดียวกัน:
 *   concurrency limit, pause/stop, record/log, task processor pattern (processorRef)
 */

const VERSIONS: MigrationVersion[] = ["v1", "v2"];
/** Phase B: ExcelJS parse blocks main thread — 2 concurrent workbooks is a safe ceiling */
const CONCURRENCY = 2;
/** Phase A downloads + decodes large base64 files — keep sequential to avoid memory spikes */
const CONCURRENCY_PULL = 1;
const MAX_LOG = 200;
/** Panel active/inactive แชร์ hook เดียวกัน แต่ progress ต้องแยก — ไม่งั้นทับกัน */
const checkpointKey = (activeFilter: 0 | 1) =>
  activeFilter === 1 ? "part-migration-checkpoint" : "part-migration-checkpoint-inactive";

export type MigrationMode = "dry-run" | "commit";
export type ItemStatus = "ok" | "warning" | "error" | "skipped";
export type MigrationPhase =
  | "idle"
  | "running"
  | "paused"
  | "need-token"
  | "done";
export type MigrationOp = "pull" | "import" | "legacy" | null;

export type MigrationLog = {
  partId: number;
  supplierCode: string;
  version: MigrationVersion;
  status: ItemStatus;
  warnings: number;
  message: string;
  partNo?: string;
  ac?: string;
  rt?: string;
};

export type MigrationCounters = {
  total: number;
  processed: number;
  ok: number;
  warning: number;
  error: number;
  skipped: number;
};

export type CheckpointData = {
  statusMap: Record<string, ItemStatus>;
  mode: MigrationMode;
  partOffset?: number;
  partLimit?: number;
};

const emptyCounters = (): MigrationCounters => ({
  total: 0,
  processed: 0,
  ok: 0,
  warning: 0,
  error: 0,
  skipped: 0,
});

type Task = {
  partId: number;
  supplierCode: string;
  version: MigrationVersion;
};

type TaskOutcome =
  | { kind: "done"; log: MigrationLog }
  | { kind: "token-expired" };

type TaskProcessor = (task: Task, runMode: MigrationMode) => Promise<TaskOutcome>;

const taskKey = (t: Task) => `${t.partId}:${t.version}`;

const loadCheckpoint = (key: string): CheckpointData | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return null;
    return parsed as CheckpointData;
  } catch {
    return null;
  }
};

const saveCheckpoint = (
  key: string,
  statusMap: Map<string, ItemStatus>,
  mode: MigrationMode,
  partOffset?: number,
  partLimit?: number,
) => {
  const data: CheckpointData = {
    statusMap: Object.fromEntries(statusMap),
    mode,
    partOffset,
    partLimit,
  };
  localStorage.setItem(key, JSON.stringify(data));
};

const countsFromMap = (statusMap: Map<string, ItemStatus>) => {
  let ok = 0, warning = 0, error = 0, skipped = 0;
  for (const s of statusMap.values()) {
    if (s === "ok") ok++;
    else if (s === "warning") warning++;
    else if (s === "error") error++;
    else if (s === "skipped") skipped++;
  }
  return { ok, warning, error, skipped };
};

export const usePartMigration = (activeFilter: 0 | 1 = 1) => {
  const CHECKPOINT_KEY = checkpointKey(activeFilter);

  const [phase, setPhase] = useState<MigrationPhase>("idle");
  const [mode, setMode] = useState<MigrationMode>("dry-run");
  const [currentOp, setCurrentOp] = useState<MigrationOp>(null);
  const [counters, setCounters] = useState<MigrationCounters>(emptyCounters());
  const [log, setLog] = useState<MigrationLog[]>([]);
  const [dirName, setDirName] = useState<string | null>(null);

  const stopRef = useRef(false);
  const needTokenRef = useRef(false);
  const modeRef = useRef<MigrationMode>("dry-run");
  const legacyTokenRef = useRef("");
  const tasksRef = useRef<Task[]>([]);
  const statusMapRef = useRef<Map<string, ItemStatus>>(new Map());
  const resultsMapRef = useRef<Map<string, MigrationLog>>(new Map());
  const saveCounterRef = useRef(0);
  const processorRef = useRef<TaskProcessor | null>(null);
  /** Concurrency for the current run — set by startPull/startImport/start before runLoop */
  const runConcurrencyRef = useRef(CONCURRENCY);
  /** Phase A uses the filesystem as checkpoint, so localStorage save is unnecessary */
  const runSkipCheckpointRef = useRef(false);
  /** In-memory log buffer — drained to state on a throttled timer to limit re-renders */
  const logBufferRef = useRef<MigrationLog[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Flush buffered log + recalculate counters from statusMap.
   * Called on a 150 ms throttle inside record(), and immediately at the end of runLoop.
   * Recalculating from the Map is O(n) but n is bounded (tasks processed so far) and
   * avoids the delta-tracking complexity that was prone to counter drift.
   */
  const flushState = useCallback(() => {
    flushTimerRef.current = null;
    const { ok, warning, error, skipped } = countsFromMap(statusMapRef.current);
    setCounters((c) => ({
      ...c,
      processed: ok + warning + error + skipped,
      ok,
      warning,
      error,
      skipped,
    }));
    setLog([...logBufferRef.current]);
  }, []);

  const record = useCallback((entry: MigrationLog) => {
    const key = `${entry.partId}:${entry.version}`;
    statusMapRef.current.set(key, entry.status);

    // Phase A (pull): only errors are actionable — skip storing ok/skipped in
    // resultsMapRef and logBufferRef to avoid thousands of unnecessary allocations.
    // Phase B keeps all entries for the CSV report and log table.
    const isPhaseA = runSkipCheckpointRef.current;
    const shouldRecord = !isPhaseA || entry.status === "error";

    if (shouldRecord) {
      resultsMapRef.current.set(key, entry);
      logBufferRef.current = [entry, ...logBufferRef.current];
      if (logBufferRef.current.length > MAX_LOG) {
        logBufferRef.current = logBufferRef.current.slice(0, MAX_LOG);
      }
    }

    // Throttle React state updates to ≤7 renders/sec instead of N renders/sec
    // (previously every task triggered setLog + setCounters → re-render a 200-row table)
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushState, 150);
    }
  }, [flushState]);

  // ─── Core loop (processor injected via processorRef) ─────────────────────────

  const runLoop = useCallback(async (runMode: MigrationMode) => {
    stopRef.current = false;
    needTokenRef.current = false;
    saveCounterRef.current = 0;
    setPhase("running");

    const concurrency = runConcurrencyRef.current;
    const skipCheckpoint = runSkipCheckpointRef.current;

    const pending = tasksRef.current.filter((t) => {
      const s = statusMapRef.current.get(taskKey(t));
      return s === undefined || s === "error";
    });

    let cursor = 0;
    const worker = async () => {
      while (cursor < pending.length && !stopRef.current) {
        const task = pending[cursor++];
        const outcome = await processorRef.current!(task, runMode);
        if (outcome.kind === "token-expired") {
          needTokenRef.current = true;
          stopRef.current = true;
          return;
        }
        record(outcome.log);
        if (!skipCheckpoint && outcome.log.status !== "error") {
          saveCounterRef.current++;
          if (saveCounterRef.current % 50 === 0) {
            saveCheckpoint(CHECKPOINT_KEY, statusMapRef.current, runMode);
          }
        }
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    if (!skipCheckpoint) {
      saveCheckpoint(CHECKPOINT_KEY, statusMapRef.current, runMode);
    }

    // Force-flush any buffered log/counter updates before changing phase
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushState();
    }

    if (needTokenRef.current) setPhase("need-token");
    else if (stopRef.current) setPhase("paused");
    else setPhase("done");
  }, [record, flushState, CHECKPOINT_KEY]);

  // ─── Phase A: Pull to disk ────────────────────────────────────────────────────

  const startPull = useCallback(
    async (
      dir: FileSystemDirectoryHandle,
      supplierCode?: string,
      versions: MigrationVersion[] = VERSIONS,
    ) => {
      processorRef.current = async (task): Promise<TaskOutcome> => {
        const base = { partId: task.partId, supplierCode: task.supplierCode, version: task.version };
        try {
          const name = toFileName(task.partId, task.version, task.supplierCode);
          if (await fileExists(dir, name)) {
            return { kind: "done", log: { ...base, status: "skipped", warnings: 0, message: "มีอยู่แล้ว" } };
          }
          const { fileArray } = await getOldCbdFile(task.partId, task.version, legacyTokenRef.current);
          const bytes = base64ToUint8Array(fileArray);
          await writeXlsx(dir, name, bytes);
          return { kind: "done", log: { ...base, status: "ok", warnings: 0, message: name } };
        } catch (err) {
          const detail = axios.isAxiosError(err)
            ? (err.response?.data as { detail?: string } | undefined)?.detail
            : undefined;
          if (detail === LEGACY_TOKEN_EXPIRED) return { kind: "token-expired" };
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            return { kind: "done", log: { ...base, status: "skipped", warnings: 0, message: "ไม่มีข้อมูล" } };
          }
          const message = detail ? String(detail) : err instanceof Error ? err.message : "unknown error";
          return { kind: "done", log: { ...base, status: "error", warnings: 0, message } };
        }
      };

      setCurrentOp("pull");
      setDirName(dir.name);
      modeRef.current = "dry-run";

      setPhase("running");
      const refs = await getOldPartIds(supplierCode, activeFilter);
      tasksRef.current = refs.flatMap((r) =>
        versions.map((version) => ({ partId: r.part_id, supplierCode: r.supplier_code, version })),
      );

      // dir เป็น checkpoint ของตัวเอง (fileExists check) ไม่ seed จาก localStorage
      statusMapRef.current = new Map();
      resultsMapRef.current = new Map();
      logBufferRef.current = [];
      // Phase A: filesystem is the checkpoint — skip localStorage saves; use lower concurrency
      // to reduce peak memory from simultaneous base64 decodes (each file ≈ 3× file-size in RAM)
      runConcurrencyRef.current = CONCURRENCY_PULL;
      runSkipCheckpointRef.current = true;
      setLog([]);
      setCounters({ ...emptyCounters(), total: tasksRef.current.length });

      await runLoop("dry-run");
    },
    [runLoop, activeFilter],
  );

  // ─── Phase B: Import from disk ────────────────────────────────────────────────

  const startImport = useCallback(
    async (dir: FileSystemDirectoryHandle, runMode: MigrationMode) => {
      processorRef.current = async (task, rm): Promise<TaskOutcome> => {
        const base = { partId: task.partId, supplierCode: task.supplierCode, version: task.version };
        try {
          const name = toFileName(task.partId, task.version, task.supplierCode);
          const file = await readAsFile(dir, name);
          let partNo = "", ac = "", rt = "";

          let softNote: { count: number; message: string } | null = null;

          if (task.version === "v2") {
            const parsed = await parsePartFromExcel(file, task.supplierCode, true, activeFilter === 1);
            ({ part_no: partNo, ac, rt } = parsed.preview.header);
            // material-ambiguous / material-resolved-legacy are auto-resolved (legacy lookup,
            // or latest-id fallback) — not a reason to block a migration run that's cheap to
            // re-check anyway (pass = file deleted).
            const softTypes = new Set(["material-ambiguous", "material-resolved-legacy"]);
            const blockingWarnings = parsed.warnings.filter((w) => !softTypes.has(w.type));
            if (blockingWarnings.length > 0) {
              const msgs = [...new Set(blockingWarnings.map((w) => w.message))];
              return { kind: "done", log: { ...base, status: "warning", warnings: blockingWarnings.length, message: msgs.join(", "), partNo, ac, rt } };
            }
            const soft = parsed.warnings.filter((w) => softTypes.has(w.type));
            if (soft.length > 0) {
              softNote = { count: soft.length, message: [...new Set(soft.map((w) => w.message))].join(", ") };
            }
            if (rm === "commit") await submitImportPart(parsed.body, task.supplierCode);
          } else {
            const parsed = await parsePartV1FromExcel(file, task.supplierCode, true, activeFilter === 1);
            ({ part_no: partNo, ac, rt } = parsed.preview.header);
            if (parsed.warnings.length > 0) {
              const msgs = [...new Set(parsed.warnings.map((w) => w.message))];
              return { kind: "done", log: { ...base, status: "warning", warnings: parsed.warnings.length, message: msgs.join(", "), partNo, ac, rt } };
            }
            if (rm === "commit") await submitImportPartV1(parsed.body, task.supplierCode);
          }
          if (rm === "commit") await deleteXlsx(dir, name);
          return {
            kind: "done",
            log: { ...base, status: "ok", warnings: softNote?.count ?? 0, message: softNote?.message ?? "", partNo, ac, rt },
          };
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 400) {
            await deleteXlsx(dir, toFileName(task.partId, task.version, task.supplierCode));
            return { kind: "done", log: { ...base, status: "skipped", warnings: 0, message: "Part มีอยู่แล้ว — ลบไฟล์แล้ว" } };
          }
          const message = err instanceof Error ? err.message : "unknown error";
          return { kind: "done", log: { ...base, status: "error", warnings: 0, message } };
        }
      };

      setCurrentOp("import");
      setDirName(dir.name);
      modeRef.current = runMode;
      setMode(runMode);

      const files = await listXlsx(dir);
      const tasks: Task[] = files.flatMap((name) => {
        const parsed = parseFileName(name);
        if (!parsed) return [];
        return [{ partId: parsed.partId, version: parsed.version, supplierCode: parsed.supplierCode }];
      });
      tasksRef.current = tasks;

      // commit mode: seed จาก checkpoint เดิม (skip รายการที่ import แล้ว)
      const statusMap = new Map<string, ItemStatus>();
      if (runMode === "commit") {
        const cp = loadCheckpoint(CHECKPOINT_KEY);
        if (cp?.mode === "commit") {
          for (const [k, s] of Object.entries(cp.statusMap)) {
            statusMap.set(k, s as ItemStatus);
          }
        }
      }
      statusMapRef.current = statusMap;
      resultsMapRef.current = new Map();
      logBufferRef.current = [];
      runConcurrencyRef.current = CONCURRENCY;
      runSkipCheckpointRef.current = false;
      setLog([]);

      const { ok, warning, skipped } = countsFromMap(statusMap);
      setCounters({
        ...emptyCounters(),
        total: tasks.length,
        processed: ok + warning + skipped,
        ok,
        warning,
        skipped,
      });

      await runLoop(runMode);
    },
    [runLoop, activeFilter, CHECKPOINT_KEY],
  );

  const resume = useCallback(
    async (newToken?: string) => {
      if (newToken) legacyTokenRef.current = newToken;
      await runLoop(modeRef.current);
    },
    [runLoop],
  );

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const setLegacyToken = useCallback((token: string) => {
    legacyTokenRef.current = token;
  }, []);

  const resetCheckpoint = useCallback(() => {
    statusMapRef.current = new Map();
    resultsMapRef.current = new Map();
    logBufferRef.current = [];
    localStorage.removeItem(CHECKPOINT_KEY);
    setLog([]);
    setCounters(emptyCounters());
    setPhase("idle");
  }, [CHECKPOINT_KEY]);

  const downloadReport = useCallback(() => {
    const rows = [
      "part_id,supplier_code,version,status,warnings,message",
      ...[...resultsMapRef.current.values()].map(
        (r) =>
          `${r.partId},${r.supplierCode},${r.version},${r.status},${r.warnings},"${r.message.replace(/"/g, "'")}"`,
      ),
    ];
    const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `migration-report-${mode}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [mode]);

  const exportPartsByVersion = useCallback((version: MigrationVersion) => {
    const rows = [
      "part_no,ac,rt,supplier_code,status,message",
      ...[...resultsMapRef.current.values()]
        .filter((r) => r.version === version)
        .map(
          (r) =>
            `"${(r.partNo ?? "").replace(/"/g, "'")}","${(r.ac ?? "").replace(/"/g, "'")}","${(r.rt ?? "").replace(/"/g, "'")}",${r.supplierCode},${r.status},"${r.message.replace(/"/g, "'")}"`,
        ),
    ];
    const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `migration-parts-${version}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return {
    phase,
    mode,
    currentOp,
    dirName,
    counters,
    log,
    startPull,
    startImport,
    resume,
    stop,
    setLegacyToken,
    resetCheckpoint,
    downloadReport,
    exportPartsByVersion,
  };
};
